import { ChevronDownIcon, ExternalLinkIcon, LinkIcon } from "@chakra-ui/icons";
import {
  Flex,
  chakra,
  Box,
  Container,
  Grid,
  ButtonGroup,
  Image,
  Text,
  Heading,
  Button,
  useToken,
  Icon,
  Spacer,
  useBreakpoint,
  useBreakpointValue,
} from "@chakra-ui/react";
import { useMotionValue, useMotionValueEvent, useScroll } from "framer-motion";
import { useEffect, useState } from "react";
import { FaShare } from "react-icons/fa";
import { InstantSearch, useHits } from "react-instantsearch-hooks";
import ExpandableSection from "@/components/ExpandableSection";
import Footer from "@/components/Footer";
import Navbar from "@/components/Navbar";
import PageInfo from "@/components/PageInfo";
import {
  CollapsibleFilter,
  FilterSection,
  RefinementList,
  Toggle,
  SearchBox,
  Pagination,
} from "@/components/SearchTools";
import { LinkButton } from "@/components/shared/generic";
import ModalAuth, { ScrollAuthWall } from "@/components/user/ModalAuth";
import { useUserData, withUserData } from "@/contexts/UserDataContext";
import searchClient from "@/utils/algolia-init";
import { getRandomColor } from "@/utils/string";

const Hero = () => (
  <Flex
    direction="column"
    pos="relative"
    w="100vw"
    justify="space-between"
    h={{ base: "90vh", md: "100vh" }}
    bg="linear-gradient(to top left, #30A1AE, #1D6C42)"
  >
    <Navbar inverted />
    <Spacer />
    <Container textAlign="center">
      <chakra.h1
        color="white"
        textStyle="pageHeader"
        lineHeight="1.25"
        fontSize={{ base: "4xl", md: "6xl" }}
      >
        World&apos;s Largest Database of Summer Programs &amp; Internships
      </chakra.h1>
    </Container>
    <Spacer />
    <Container textAlign="center">
      <chakra.h2 color="white" fontSize={{ base: "xl", md: "3xl" }}>
        Search by grade, location, industry, and more to find the perfect internship for you.
      </chakra.h2>
    </Container>
    <Image
      alignSelf="center"
      alt="Splash"
      src="/images/search/splash.png"
      h="15vh"
      mb={-4}
      draggable={false}
      display="inline-block"
    />
  </Flex>
);

const FilterArea = () => (
  <Grid
    templateColumns={{ base: "1fr", md: "3fr 1fr 1fr 1fr" }}
    overflow="visible"
    gap={4}
    pl={{ base: 2, md: 0 }}
    mt={4}
  >
    {/* Make the box take up all of the row */}
    <Box gridColumn="1/-1" mt={{ base: 4, md: 0 }}>
      <SearchBox
        header="Search by Organization, University, or Keyword"
        placeholder="Type in an organization (like NASA) or a university (like Columbia) to see their internships!"
      />
    </Box>
    <CollapsibleFilter header="Subject Area" color="purple.700">
      <RefinementList margin attribute="fields" />
    </CollapsibleFilter>
    <CollapsibleFilter header="Grade" color="red.700">
      <ButtonGroup isAttached>
        <RefinementList attribute="grade" sorted="numeric" />
      </ButtonGroup>
    </CollapsibleFilter>
    <CollapsibleFilter header="Fees" color="blue.700">
      <ButtonGroup isAttached>
        <Toggle title="No Cost" attribute="is_free" />
        <Toggle title="Has Fees" attribute="is_free_not" />
      </ButtonGroup>
    </CollapsibleFilter>
  </Grid>
);

const Hit = ({ organization, name, url, description, location, fields, cost, salary }) => {
  const [color, setColor] = useState("black");
  const [learnMoreText, setLearnMoreText] = useState("Apply");
  const [learnMoreIcon, setLearnMoreIcon] = useState(<LinkIcon />);
  const darkColor = useToken("colors", color + ".500");
  const { currentUser } = useUserData();

  useEffect(() => {
    const colors = ["black", "green", "blue", "purple"];
    setColor(colors[Math.floor(Math.random() * colors.length)]);
    const learnMoreTexts = ["Apply Now", "Visit Website", "Contact Program"];
    const learnMoreIcons = [
      <ExternalLinkIcon key={0} />,
      <LinkIcon key={1} />,
      <Icon key={2} as={FaShare} />,
    ];
    const learnMoreIndex = Math.floor(Math.random() * learnMoreTexts.length);
    setLearnMoreText(learnMoreTexts[learnMoreIndex]);
    setLearnMoreIcon(learnMoreIcons[learnMoreIndex]);
  }, []);

  const onClick = () => {
    window.open(url, "_blank");
  };

  return (
    <Box
      p={4}
      bg="white"
      borderRadius="lg"
      borderTop={`1px solid ${darkColor}`}
      borderRight={`1px solid ${darkColor}`}
      borderBottom={`0.25rem solid ${darkColor}`}
      borderLeft={`0.25rem solid ${darkColor}`}
    >
      <Heading as="h3" fontSize={{ base: "lg", md: "2xl" }}>
        {organization ?? name}
      </Heading>
      <Text mb={4} fontSize="lg" fontWeight="bold">
        {!!organization && name}
      </Text>
      {description && (
        <Text mb={4} fontSize="lg">
          <ExpandableSection content={description} color="blackAlpha.500" />
        </Text>
      )}
      {location && (
        <Text>
          <chakra.b>Location:&nbsp;</chakra.b>
          {location}
        </Text>
      )}
      {fields && (
        <Text>
          <chakra.b>Industry:&nbsp;</chakra.b>
          {fields.join(", ")}
        </Text>
      )}
      {cost && (
        <Text>
          <chakra.b>Cost:&nbsp;</chakra.b>
          {cost}
        </Text>
      )}
      {salary && (
        <Text>
          <chakra.b>Salary:&nbsp;</chakra.b>
          {salary}
        </Text>
      )}
      {url && (
        <Button colorScheme={color} mt={2} leftIcon={learnMoreIcon} onClick={onClick}>
          {learnMoreText}
        </Button>
      )}
    </Box>
  );
};

const Hits = () => {
  const { hits } = useHits();
  if (hits.length === 0) {
    return (
      <Box textAlign="center" mt={8}>
        <Heading as="h3" fontSize={{ base: "lg", md: "2xl" }}>
          No results found.
        </Heading>
        <Text mt={4} fontSize="lg">
          Try searching for something else.
        </Text>
      </Box>
    );
  }
  return (
    <Flex direction="column" gap={4}>
      {hits.map((hit, i) => (
        <Hit key={i} {...hit} />
      ))}
    </Flex>
  );
};

const InternshipDatabase = () => {
  return (
    <>
      <PageInfo
        overrideOGImage="https://admityogi.com/images/summerprograms/og.png"
        title="The World's Largest Database Internships & Summer Programs for High Schoolers"
        description="Search by grade, location, industry, and more to find the perfect opportunity for you."
      />
      <ScrollAuthWall base={1000} md={1300} />
      <Hero />
      <InstantSearch searchClient={searchClient} indexName="internship_list">
        <Container maxW="container.lg" id="results" my={8}>
          <Heading as="h2" fontSize={{ base: "xl", md: "3xl" }}>
            Our Mission
          </Heading>
          <Text mt={4} mb={4} fontSize="xl">
            It can be hard to find prestigious summer activities as a high schooler to level up your
            college application. To help you find the perfect one, we&apos;ve hand-curated a
            database of prestigious internships, research opportunities, summer programs. Admissions
            officers already know of these impressive programs, so doing one will look great to
            them!
          </Text>
          <FilterSection>
            <FilterArea />
          </FilterSection>
        </Container>
        <Container maxW="container.lg" pb={10}>
          <Pagination showNResults={false} />
          <Hits />
        </Container>
      </InstantSearch>
      <Footer />
    </>
  );
};

export default withUserData(InternshipDatabase);
